import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { ChatPanel } from './chat/ChatPanel';
import { FOCUS_RING_CLASSES, trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';
import { useKeyboardInset } from '../hooks/useKeyboardInset';

interface CaptureSheetProps {
  /** Closes the sheet (backdrop, X, Escape); Layout returns focus to the FAB. */
  onClose: () => void;
}

/**
 * Quick-chat sheet opened by the global FAB. Owns only the dialog chrome
 * (overlay, panel, focus trap, Escape); the body is the shared ChatPanel
 * starting a FRESH session — the same one code path as the /alma page.
 * Follow-ups and approvals work right here; "Continue in chat" jumps to the
 * full page for the same session. The sheet dies on close; the session
 * itself is durable and lives on in History.
 */
export function CaptureSheet({ onClose }: CaptureSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  useScrollLock(true);
  const keyboardInset = useKeyboardInset(true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  const continueInChat = (): void => {
    if (!sessionId) return;
    onClose();
    navigate(`/alma/${sessionId}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      style={keyboardInset ? { paddingBottom: keyboardInset } : undefined}
    >
      <div
        ref={panelRef}
        className="glass-panel bg-[var(--surface)] w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 shadow-warm animate-sheet-up max-h-[min(85dvh,100%)] overflow-y-auto overscroll-contain safe-bottom"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-sheet-title"
        tabIndex={-1}
      >
        <div
          aria-hidden
          className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[var(--border)] sm:hidden"
        />
        <div className="flex items-center justify-between mb-4">
          <h2 id="capture-sheet-title" className="text-xl font-semibold text-[var(--text-primary)]">
            Tell me anything
          </h2>
          <div className="flex items-center gap-1">
            {sessionId && (
              <button
                type="button"
                onClick={continueInChat}
                className={`btn btn-ghost text-sm ${FOCUS_RING_CLASSES}`}
              >
                Continue in chat
                <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors ${FOCUS_RING_CLASSES}`}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <ChatPanel autoFocus onSessionCreated={setSessionId} />
        </div>
      </div>
    </div>
  );
}
